import {
	CalendarTodayOutlined,
	Event,
	KeyboardArrowDownOutlined,
	KeyboardArrowRightOutlined
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Collapse, FormControl, FormLabel, TextField, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid2";
import InputAdornment from "@mui/material/InputAdornment";
import { CalendarIcon, DatePicker, DateRangePicker, SingleInputDateRangeField } from "@mui/x-date-pickers-pro";
import axios from "axios";
import dayjs from "dayjs";
import _ from "lodash";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import FormBoxTitle from "../../../../components/mui/app/formBoxTitle/formBoxTitle";
import CustomAutocomplete from "../../../../components/mui/form/autocomplete/autocomplete";
import DateRange from "../../../../components/mui/form/dateRange/dateRange";
import { API } from "../../../../helpers/constants";
import { handleAxiosErrors, logoutUnauthorizedUser } from "../../../../helpers/errorHandling";
import { history_filter_customer } from "../../../../helpers/rights/rights";
import { formControlDefaults, textFieldDefaults } from "../../../../helpers/theme/defaultProps";
import useUserAction from "../../../../hooks/user/useUserAction";
import useUserData from "../../../../hooks/user/useUserData";
import PermissionHOC from "../../../permission/components/permissionHOC/permissionHOC";
import { useHasPermission } from "../../../permission/hooks/useHasPermission";
import { ISSUES_GLOBAL } from "../../locales/namespaces";
import { mapFilters } from "../../pages/index";

const IssueFilters = (props) => {
	const {
		initialFilters,
		filters,
		setFilters,
		dataManager,
		loading,
		sites,
		customers,
		statuses,
		types,
		categories,
		assignees,
		internalAssignments,
		kinds,
		technicians,
		onSearchCompleted
	} = props
	const { t } = useTranslation(ISSUES_GLOBAL)
	const { dateFormat, token, isLoggedIn } = useUserData()
	const { userLogout } = useUserAction()
	const [loadingSearch, setLoadingSearch] = useState(false)
	const [customersData, setCustomersData] = useState([...customers])
	const [sitesData, setSitesData] = useState([...sites])
	const [typesData, setTypesData] = useState([...types])
	const [categoriesData, setCategoriesData] = useState([...categories])
	const [assigneesData, setAssigneesData] = useState([...assignees])
	const [internalAssignmentsData, setInternalAssignmentsData] = useState([...(internalAssignments ?? [])])
	const [kindsData] = useState([...(kinds ?? [])])
	const [techniciansData] = useState([...(technicians ?? [])])
	const [open, setOpen] = useState(true)
	const viewCustomerFilter = useHasPermission([history_filter_customer], { shouldView: true })
	
	const searchCustomer = useCallback((evt) => {
		setLoadingSearch(true)
		axios({
			method: 'post',
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
			data: {
				query: evt.target.value
			},
			url: `${API}/en/customers/search`
		}).then((response) => {
			setLoadingSearch(false)
			setCustomersData(response.data.data)
		}).catch((error) => {
			enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
				variant: 'error',
				autoHideDuration: 3000,
			})
			setLoadingSearch(false)
			logoutUnauthorizedUser(error, isLoggedIn, userLogout)
		})
	}, [t, isLoggedIn, userLogout])

	const searchAssignees = useCallback((evt) => {
		// Accept both single-object customer and array of customers; return comma-separated ids
		const getIds = (value) => {
			if (Array.isArray(value) && value.length > 0) return value.map(i => i?.id).join(',')
			if (value && typeof value === 'object' && value.id) return String(value.id)
			return ''
		}
		const customerIds = getIds(filters.customerFilter)
		setLoadingSearch(true)
		axios({
			method: 'post',
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
			url: `${API}/en/custom/action`,
			data: {
				action_id: 'assignee-search',
				fields: {
					CustId: customerIds,
					query: evt.target.value
				},
			},
		}).then((response) => {
			setAssigneesData(response.data.data)
			setLoadingSearch(false)
		}).catch((error) => {
			setLoadingSearch(false)
			enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
				variant: 'error',
				autoHideDuration: 3000,
			})
		})
	}, [filters.customerFilter, t])
	
	const searchInternalAssignments = useCallback((evt) => {
		setLoadingSearch(true)
		axios({
			method: 'post',
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
			url: `${API}/en/issue/assignee/search`,
			data: {
				query: evt.target.value,
				Internal: true
			},
		}).then((response) => {
			setInternalAssignmentsData(response.data.data)
			setLoadingSearch(false)
		}).catch((error) => {
			setLoadingSearch(false)
			enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
				variant: 'error',
				autoHideDuration: 3000,
			})
			logoutUnauthorizedUser(error, isLoggedIn, userLogout)
		})
	}, [t, isLoggedIn, userLogout])
	
	const searchSite = useCallback((evt, name) => {
		setLoadingSearch(true)
		axios({
			method: 'post',
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
			data: {
				query: evt.target.value,
				...(filters.customerFilter && filters.customerFilter?.id && { CustId: filters.customerFilter?.id }),
				Type: 'Site',
			},
			url: `${API}/en/customer/address/search`
		}).then((response) => {
			setSitesData(response.data.data)
			setLoadingSearch(false)
		}).catch((error) => {
			setLoadingSearch(false)
			enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
				variant: 'error',
				autoHideDuration: 3000,
			})
			logoutUnauthorizedUser(error, isLoggedIn, userLogout)
		})
	}, [t, isLoggedIn, userLogout, filters.customerFilter])
	
	const onChangeCustomer = useCallback((newValue) => {
		setLoadingSearch(true)
		axios({
			method: 'post',
			data: {
				CustId: newValue?.id,
				ReturnData: ['addresses']
			},
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
			url: `${API}/en/issues/issueCustData`
		}).then((res) => {
			setLoadingSearch(false)
			setSitesData(_.orderBy(res.data?.addresses?.map(i => ({
				id: i.id,
				Description: i.Site
			})), ['Description'], ['asc']))
		}).catch((error) => {
			setLoadingSearch(false)
			enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
				variant: 'error',
				autoHideDuration: 3000,
			})
			logoutUnauthorizedUser(error, isLoggedIn, userLogout)
		})
	}, [t, isLoggedIn, userLogout])
	
	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', marginBottom: 1 }}>
			<FormBoxTitle
				title={t('Filter Options')}
			>
				<Typography
					variant={'button'}
					component={'div'}
					onClick={() => setOpen(!open)}
					sx={{
						cursor: 'pointer',
						padding: 0.5,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center'
					}}
				>
					{open ? <KeyboardArrowDownOutlined sx={{ fontSize: 16 }}/> :
						<KeyboardArrowRightOutlined sx={{ fontSize: 16 }}/>}
				</Typography>
			</FormBoxTitle>
			<Collapse in={open}>
				<Grid container spacing={1} alignItems={'flex-end'}>
					<PermissionHOC requiredPermissions={[history_filter_customer]}>
						<Grid size={{ xs: 12, sm: 6, md: viewCustomerFilter ? 4 : 6, lg: viewCustomerFilter ? 4 : 6 }}>
							<FormControl
								{...formControlDefaults}
							>
								<FormLabel>{t('Customer')}</FormLabel>
								<CustomAutocomplete
									identifier="id"
									name="customerFilter"
									onChange={(evt, newValue, reason) => {
										setFilters({
											...filters,
											customerFilter: newValue
										})
										if (reason === 'clear') {
											setCustomersData([...customers])
											setSitesData([...sites])
										} else {
											onChangeCustomer(newValue)
										}
									}}
									value={filters.customerFilter}
									options={customersData}
									labelKey={'Description'}
									otherprops={{
										selectOnFocus: true,
										handleHomeEndKeys: true,
										getOptionKey: (option) => (option?.id)
									}}
									loading={loadingSearch}
									onInputChange={(evt) => searchCustomer(evt)}
								/>
							</FormControl>
						</Grid>
					</PermissionHOC>
					<Grid size={{ xs: 12, sm: 6, md: viewCustomerFilter ? 4 : 6, lg: viewCustomerFilter ? 4 : 6 }}>
						<FormControl
							{...formControlDefaults}
						>
							<FormLabel>{t('Site')}</FormLabel>
							<CustomAutocomplete
								identifier="id"
								name="siteFilter"
								onChange={(evt, newValue, reason) => {
									setFilters({
										...filters,
										siteFilter: newValue
									})
									if (reason === 'clear' && !filters.customerFilter?.id) {
										setSitesData([...sites])
									}
								}}
								value={filters.siteFilter}
								options={sitesData}
								labelKey={'Description'}  //LoginName or AssigneeName
								placeholder={t('Select one or more..')}
								otherprops={{
									multiple: true,
									disableCloseOnSelect: true,
									limitTags: 1,
									getOptionKey: (option) => (option?.id)
								}}
								loading={loadingSearch}
								onInputChange={(evt) => searchSite(evt)}
							/>
						</FormControl>
					</Grid>
					<Grid size={{ xs: 12, sm: 6, md: viewCustomerFilter ? 4 : 6, lg: viewCustomerFilter ? 4 : 6 }}>
						<FormControl
							{...formControlDefaults}
						>
							<FormLabel>{t('Status')}</FormLabel>
							<CustomAutocomplete
								identifier="id"
								name="statusFilter"
								onChange={(evt, newValue, reason) => {
									setFilters({
										...filters,
										statusFilter: newValue
									})
								}}
								value={filters.statusFilter}
								options={statuses}
								labelKey={'Description'}  //LoginName or AssigneeName
								placeholder={t('Select one or more..')}
								otherprops={{
									multiple: true,
									disableCloseOnSelect: true,
									limitTags: 1,
									getOptionKey: (option) => (option?.id)
								}}
							/>
						</FormControl>
					</Grid>
					<Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
						<FormControl {...formControlDefaults}>
							<FormLabel>{t('Targeted Start')}</FormLabel>
							<DateRange
								dateFormat={dateFormat}
								value={filters.targetedStartDt}
								onChange={(newValue) => {
									setFilters(prev => ({
										...prev,
										targetedStartDt: newValue
									}))
								}}
								onClear={() => (
									setFilters(prev => ({
										...prev,
										targetedStartDt: null
									}))
								)}
							/>
						</FormControl>
					</Grid>
					<Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
						<FormControl {...formControlDefaults}>
							<FormLabel>{t('Targeted Expiry')}</FormLabel>
							<DateRange
								dateFormat={dateFormat}
								value={filters.targetedEndDt}
								onChange={(newValue) => {
									setFilters(prev => ({
										...prev,
										targetedEndDt: newValue
									}))
								}}
								onClear={() => (
									setFilters(prev => ({
										...prev,
										targetedEndDt: null
									}))
								)}
							/>
						</FormControl>
					</Grid>
					<Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
						<FormControl {...formControlDefaults}>
							<FormLabel>{t('Actual End')}</FormLabel>
							<DateRange
								dateFormat={dateFormat}
								value={filters.actualEndDt}
								onChange={(newValue) => {
									setFilters(prev => ({
										...prev,
										actualEndDt: newValue
									}))
								}}
								onClear={() => (
									setFilters(prev => ({
										...prev,
										actualEndDt: null
									}))
								)}
							/>
						</FormControl>
					</Grid>
					<Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
						<FormControl {...formControlDefaults}>
							<FormLabel>{t('Submit Date')}</FormLabel>
							<DateRange
								dateFormat={dateFormat}
								value={filters.submitDt}
								onChange={(newValue) => {
									setFilters(prev => ({
										...prev,
										submitDt: newValue
									}))
								}}
								onClear={() => (
									setFilters(prev => ({
										...prev,
										submitDt: null
									}))
								)}
							/>
						</FormControl>
					</Grid>
					<Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
						<FormControl
							{...formControlDefaults}
						>
							<FormLabel>{t('Code')}</FormLabel>
							<TextField
								{...textFieldDefaults}
								name={'codeFilter'}
								onChange={(evt) => {
									const newValue = evt.target.value;
									setFilters({
										...filters,
										codeFilter: newValue
									})
								}}
								value={filters.codeFilter}
							/>
						</FormControl>
					</Grid>
					<Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
						<FormControl
							{...formControlDefaults}
						>
							<FormLabel>{t('Dimension')}</FormLabel>
							<CustomAutocomplete
								identifier="id"
								name="typeFilter"
								onChange={(evt, newValue, reason) => {
									setFilters({
										...filters,
										typeFilter: newValue
									})
								}}
								value={filters.typeFilter}
								options={typesData}
								labelKey={'Description'}  //LoginName or AssigneeName
								placeholder={t('Select one or more..')}
								otherprops={{
									multiple: true,
									disableCloseOnSelect: true,
									limitTags: 1,
									getOptionKey: (option) => (option?.id)
								}}
							/>
						</FormControl>
					</Grid>
					<Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
						<FormControl
							{...formControlDefaults}
						>
  					<FormLabel>{t('Service Category')}</FormLabel>
  					<CustomAutocomplete
  						identifier="id"
  						name="categoriesFilter"
  						onChange={(evt, newValue, reason) => {
  							setFilters({
  								...filters,
  								categoriesFilter: newValue
  							})
  						}}
  						value={filters.categoriesFilter}
  						options={categoriesData}
  						labelKey={'Description'}
  						otherprops={{
  							multiple: true,
  							disableCloseOnSelect: true,
  							limitTags: 1,
  							getOptionKey: (option) => (option?.id)
  						}}
  						loading={loadingSearch}
  					/>
  				</FormControl>
  			</Grid>
  			<Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
  				<FormControl
  					{...formControlDefaults}
  				>
   			<FormLabel>{t('Subcontractor')}</FormLabel>
  					<CustomAutocomplete
  						identifier="id"
  						name="assigneesFilter"
  						onChange={(evt, newValue, reason) => {
  							setFilters({
  								...filters,
  								assigneesFilter: newValue
  							})
  						}}
  						value={filters.assigneesFilter}
  						options={assigneesData}
  						labelKey={'Description'}
  						otherprops={{
  							multiple: true,
  							disableCloseOnSelect: true,
  							limitTags: 1,
  							getOptionKey: (option) => (option?.id)
  						}}
					onInputChange={(evt) => searchAssignees(evt)}
  						loading={loadingSearch}
  					/>
  				</FormControl>
  			</Grid>
			<Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
  				<FormControl
  					{...formControlDefaults}
  				>
  					<FormLabel>{t('Internal Assignment')}</FormLabel>
  					<CustomAutocomplete
  						identifier="id"
  						name="internalAssignmentsFilter"
  						onChange={(evt, newValue, reason) => {
  							setFilters({
  								...filters,
  								internalAssignmentsFilter: newValue
  							})
  						}}
  						value={filters.internalAssignmentsFilter}
  						options={internalAssignmentsData}
  						labelKey={'Description'}
  						otherprops={{
  							multiple: true,
  							disableCloseOnSelect: true,
  							limitTags: 1,
  							getOptionKey: (option) => (option?.id)
  						}}
					onInputChange={(evt) => searchInternalAssignments(evt)}
  						loading={loadingSearch}
  					/>
  				</FormControl>
  			</Grid>
			<Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
  				<FormControl
  					{...formControlDefaults}
  				>
  					<FormLabel>{t('Work Type')}</FormLabel>
  					<CustomAutocomplete
  						identifier="id"
  						name="kindFilter"
  						onChange={(evt, newValue, reason) => {
  							setFilters({
  								...filters,
  								kindFilter: newValue
  							})
  						}}
  						value={filters.kindFilter}
  						options={kindsData}
  						labelKey={'Description'}
  						otherprops={{
  							multiple: true,
  							disableCloseOnSelect: true,
  							limitTags: 1,
  							getOptionKey: (option) => (option?.id)
  						}}
  					/>
  				</FormControl>
  			</Grid>
					<Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
						<FormControl
							{...formControlDefaults}
						>
							<FormLabel>{t('Technicians')}</FormLabel>
							<CustomAutocomplete
								identifier="id"
								name="technicianFilter"
								onChange={(evt, newValue, reason) => {
									setFilters({
										...filters,
										technicianFilter: newValue
									})
								}}
								value={filters.technicianFilter}
								options={techniciansData}
								labelKey={'Description'}
								otherprops={{
									multiple: true,
									disableCloseOnSelect: true,
									limitTags: 1,
									getOptionKey: (option) => (option?.id)
								}}
							/>
						</FormControl>
					</Grid>
					<Grid size={{ xs: 12 }}  sx={{background: '#f6f6f6', p: 0.4}}>
						<Box display={'flex'} alignItems={'flex-end'} justifyContent={'center'}
							 flexWrap={'wrap'} gap={1}>
							<Button
								loading={loading}
								onClick={
								async () => {
									const params = { ...mapFilters(filters) };

									try {
										await dataManager(params);
										onSearchCompleted?.();
									} catch (e) {

									}
								}}
								variant="outlined"
								color={'success'}
							>
								{t('Search')}
							</Button>
							<Button
								loading={loading}
								onClick={() => {
							dataManager({
									customerFilter: null,
									statusFilter: null,
									typeFilter: null,
									codeFilter: null,
									siteFilter: null,
									categoriesFilter: null,
									assigneesFilter: null,
									internalAssignmentsFilter: null,
									kindFilter: null,
								    technicianFilter: null,
									TargetedStartDtFrom: null,
									TargetedStartDtTo: null,
									TargetedEndDtFrom: null,
									TargetedEndDtTo: null,
									ActualEndDtFrom: null,
									ActualEndDtTo: null,
									SubmitDtFrom: null,
									SubmitDtTo: null,
								})
								setFilters(initialFilters)
								setCustomersData([...customers])
								setSitesData([...sites])
								setTypesData([...types])
								setCategoriesData([...categories])
								setAssigneesData([...assignees])
								setInternalAssignmentsData([...(internalAssignments ?? [])])
								}}
								variant="outlined"
								color={'error'}
							>
								{t('Clear')}
							</Button>
						</Box>
					</Grid>
					<Grid size={12}>
						<Divider/>
					</Grid>
				</Grid>
			</Collapse>
		</Box>
	);
};

export default IssueFilters